import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { getStoredAccessToken } from '../services/authStorage';

export const ASK_EDGENTIK_CHIPS = [
  'What is happening in the restricted area?',
  'Does this keep happening?',
  'Do we have a signed Safety Control Scan?',
  'Did the fix work?',
  'What should I pay attention to?',
] as const;

const FAILURE = "I couldn't retrieve that information right now.";
export const SITE_TIME_ZONE = 'America/Toronto';

export function siteTimeZoneAbbreviation(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SITE_TIME_ZONE,
    timeZoneName: 'short',
  }).formatToParts(now);
  return parts.find((part) => part.type === 'timeZoneName')?.value || 'EDT';
}

type EvidenceItem = {
  kind: string;
  display_time: string;
  image_url: string;
};

type LinkItem = {
  label: string;
  href: string;
};

type Turn = {
  role: 'user' | 'assistant';
  text: string;
  details?: string[];
  evidence?: EvidenceItem[];
  links?: LinkItem[];
  images?: string[];
};

type ChatPayload = {
  answer?: string;
  details?: string[];
  evidence?: EvidenceItem[];
  links?: LinkItem[];
};

export type AskProductBinding = {
  siteId: string;
  conditionId: string;
};

function endpoint(path: string): string {
  if (path.startsWith('/ask-edgentik-api')) {
    return path;
  }
  return `/ask-edgentik-api${path}`;
}

async function askFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getStoredAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (init.body) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(endpoint(path), { ...init, headers });
}

function conversationIdFor(siteId: string, conditionId: string): string {
  const suffix = Math.random().toString(16).slice(2);
  if (siteId && conditionId) {
    return `product-${siteId}-${conditionId}-${suffix}`;
  }
  return `demo-${suffix}`;
}

export function AskEdgentikPanel({
  variant = 'page',
  product = null,
}: {
  variant?: 'page' | 'embedded';
  product?: AskProductBinding | null;
}) {
  const siteId = String(product?.siteId || '').trim();
  const conditionId = String(product?.conditionId || '').trim();
  const productBound = Boolean(siteId && conditionId);
  const conversationId = useMemo(
    () => conversationIdFor(siteId, conditionId),
    [siteId, conditionId],
  );
  const [draft, setDraft] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [waiting, setWaiting] = useState(false);
  const page = variant === 'page';

  useEffect(() => {
    setTurns([]);
  }, [conversationId]);

  useEffect(() => {
    if (!page && !productBound) return undefined;
    const controller = new AbortController();
    const body: Record<string, unknown> = { conversation_id: conversationId };
    if (productBound) {
      body.mode = 'product';
      body.context = { site_id: siteId, condition_id: conditionId };
    }
    askFetch('/warmup', {
      method: 'POST',
      body: JSON.stringify(body),
      signal: controller.signal,
    }).catch(() => undefined);
    return () => controller.abort();
  }, [conditionId, conversationId, page, productBound, siteId]);

  async function submit(question: string) {
    const message = question.trim();
    if (!message || waiting) return;
    if (!page && !productBound) return;
    setDraft('');
    setWaiting(true);
    setTurns((current) => [...current, { role: 'user', text: message }]);
    const body: Record<string, unknown> = { message, conversation_id: conversationId };
    if (productBound) {
      body.mode = 'product';
      body.context = { site_id: siteId, condition_id: conditionId };
    }
    try {
      const response = await askFetch('/chat', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const payload = (await response.json()) as ChatPayload;
      const answer = payload.answer || FAILURE;
      const evidence = Array.isArray(payload.evidence) ? payload.evidence : [];
      const links = Array.isArray(payload.links) ? payload.links : [];
      const details = Array.isArray(payload.details) ? payload.details : [];
      const images = await loadImages(evidence);
      setTurns((current) => [
        ...current,
        { role: 'assistant', text: answer, details, evidence, links, images },
      ]);
    } catch {
      setTurns((current) => [...current, { role: 'assistant', text: FAILURE }]);
    } finally {
      setWaiting(false);
    }
  }

  return (
    <Box
      sx={{ maxWidth: page ? 760 : 'none', mx: page ? 'auto' : 0, px: 2, py: page ? 4 : 2 }}
      data-testid={page ? 'ask-edgentik-page' : 'ask-edgentik-panel'}
      data-ask-mode={productBound ? 'product' : (page ? 'demo' : 'unbound')}
      data-condition-id={productBound ? conditionId : ''}
    >
      <Typography variant={page ? 'h4' : 'h6'} component={page ? 'h1' : 'h2'} sx={{ mb: 1 }}>
        Ask Edgentik
      </Typography>
      {page ? (
        <>
          <Box
            component="span"
            data-testid="demonstration-badge"
            sx={{
              display: 'inline-block',
              mb: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: '#F4E8C8',
              color: '#6B4E16',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
            }}
          >
            DEMONSTRATION DATA
          </Box>
          <Typography variant="body2" color="text.secondary">
            Edgentik demo environment
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="site-time-note" sx={{ display: 'block', mb: 3 }}>
            Times shown in site local time · {siteTimeZoneAbbreviation()}
          </Typography>
        </>
      ) : null}
      <Stack spacing={1.5} sx={{ mb: 3 }} data-testid="ask-edgentik-transcript">
        {turns.map((turn, index) => (
          <Box
            key={`${turn.role}-${index}`}
            data-testid={turn.role === 'user' ? 'user-message' : 'assistant-message'}
            sx={{
              alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: turn.role === 'user' ? '#E7EEF8' : '#FFFFFF',
              border: '1px solid',
              borderColor: turn.role === 'user' ? '#E7EEF8' : '#E4E7EC',
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {turn.text}
            </Typography>
            {(turn.details || []).map((line) => (
              <Typography key={line} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {line}
              </Typography>
            ))}
            {(turn.images || []).map((src) => (
              <Box
                key={src}
                component="img"
                src={src}
                alt="Newest snapshot"
                sx={{ display: 'block', mt: 1.5, maxWidth: 360, borderRadius: 1 }}
              />
            ))}
            {(turn.evidence || []).map((item) =>
              item.display_time ? (
                <Typography key={item.display_time} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {`Latest snapshot · ${item.display_time}`}
                </Typography>
              ) : null,
            )}
            {(turn.links || []).map((link) => (
              <Button key={link.href} variant="text" sx={{ textTransform: 'none', px: 0 }} onClick={() => openClip(link.href)}>
                {link.label}
              </Button>
            ))}
          </Box>
        ))}
        {waiting ? <Typography data-testid="ask-edgentik-waiting">Checking Edgentik...</Typography> : null}
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {ASK_EDGENTIK_CHIPS.map((question) => (
          <Button key={question} variant="outlined" sx={{ textTransform: 'none' }} onClick={() => submit(question)}>
            {question}
          </Button>
        ))}
      </Stack>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
        sx={{ display: 'flex', gap: 1 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Ask a question..."
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          inputProps={{ 'aria-label': 'Ask a question' }}
        />
        <Button type="submit" variant="contained" sx={{ textTransform: 'none' }}>
          Send
        </Button>
      </Box>
    </Box>
  );
}

export default function AskEdgentikPage() {
  return <AskEdgentikPanel variant="page" />;
}

async function loadImages(evidence: EvidenceItem[]): Promise<string[]> {
  const images: string[] = [];
  for (const item of evidence) {
    if (!item.image_url) {
      continue;
    }
    try {
      const response = await askFetch(item.image_url);
      if (!response.ok) {
        continue;
      }
      const blob = await response.blob();
      images.push(URL.createObjectURL(blob));
    } catch {
      continue;
    }
  }
  return images;
}

async function openClip(href: string) {
  const response = await askFetch(href);
  if (!response.ok) {
    return;
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
}
